/**
 * On-page anchor navigation custom element (<q-anchor-nav>).
 *
 * Loaded directly from this versioned asset (asset_url adds ?v=) instead of the
 * lazy island bundle, because the bundle's dynamic import is not cache-busted
 * and kept serving stale code. Uses a unique tag so it never collides with the
 * cached `anchor-navigation` island.
 *
 * Behavior: the bar is always position:fixed (toggled via .js-fixed once JS
 * runs, mirroring the theme's own sticky-tabs.liquid since position:sticky is
 * unreliable in this theme's DOM). The .is-visible class shows it once the user
 * scrolls past the bar's natural position; `top` follows the site header height.
 */
(function () {
  if (window.customElements && customElements.get('q-anchor-nav')) {
    return;
  }

  class QAnchorNav extends HTMLElement {
    constructor() {
      super();
      this.state = { activeId: null, isVisible: false, anchorTop: 0 };
      this.config = { offsetTop: 0, threshold: 0.5, scrollBehavior: 'smooth' };
      this.section = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleScroll = this.throttle(this.onScroll.bind(this), 50);
      this.handleResize = this.throttle(this.onResize.bind(this), 100);
    }

    connectedCallback() {
      try {
        this.init();
      } catch (error) {
        console.error('QAnchorNav 初始化失败:', error);
      }
    }

    disconnectedCallback() {
      this.cleanup();
    }

    init() {
      this.section = this.closest('.shopify-section');
      this.loadConfig();
      // Measure natural position while still in normal flow
      this.measureAnchor();
      this.setupEventListeners();
      this.observeSections();
      this.onScroll();
    }

    loadConfig() {
      this.config.offsetTop = parseInt(this.getAttribute('data-offset') || '0', 10);
    }

    getStickyAreaHeight() {
      const root = getComputedStyle(document.documentElement);
      let area = parseFloat(root.getPropertyValue('--sticky-area-height'));

      if (!area || Number.isNaN(area)) {
        const headerEnabled = root.getPropertyValue('--sticky-header-enabled').trim() === '1';
        const announcementEnabled = root.getPropertyValue('--sticky-announcement-bar-enabled').trim() === '1';
        
        let fallbackArea = 0;
        if (headerEnabled) {
          const header = document.querySelector('.shopify-section--header');
          fallbackArea += header ? header.getBoundingClientRect().height : 0;
        }
        if (announcementEnabled) {
          const announcement = document.querySelector('.shopify-section--announcement-bar');
          fallbackArea += announcement ? announcement.getBoundingClientRect().height : 0;
        }
        area = fallbackArea;
      }

      return area || 0;
    }

    getEffectiveTopOffset() {
      const rootStyles = getComputedStyle(document.documentElement);
      const headerVisible = rootStyles.getPropertyValue('--header-is-visible').trim() !== '0';

      if (headerVisible) {
        return this.getStickyAreaHeight();
      }

      const root = getComputedStyle(document.documentElement);
      const announcementEnabled = root.getPropertyValue('--sticky-announcement-bar-enabled').trim() === '1';
      if (!announcementEnabled) {
        return 0;
      }

      const announcement = document.querySelector('.shopify-section--announcement-bar');
      return announcement ? announcement.getBoundingClientRect().height : 0;
    }

    measureAnchor() {
      const reference = this.section || this;
      const rect = reference.getBoundingClientRect();
      this.state.anchorTop = window.scrollY + rect.top;
    }

    setupEventListeners() {
      this.addEventListener('click', this.handleClick);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      
      // Observe html element for style changes (like --header-is-visible)
      this.styleObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            this.onScroll();
          }
        });
      });
      this.styleObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

      // Re-measure once images above (e.g. the gallery) finish loading and shift layout.
      if (document.readyState !== 'complete') {
        window.addEventListener('load', this.handleResize, { once: true });
      }
    }

    onResize() {
      this.measureAnchor();
      this.onScroll();
    }

    onScroll() {
      const topOffset = this.getEffectiveTopOffset();
      const threshold = this.state.anchorTop - topOffset;
      const shouldFix = window.scrollY >= threshold;

      if (shouldFix !== this.state.isVisible) {
        this.toggleFixed(shouldFix, topOffset);
      } else if (this.state.isVisible) {
        this.style.top = `${topOffset}px`;
      }

      this.checkActiveSections();
    }

    toggleFixed(shouldFix, topOffset) {
      this.state.isVisible = shouldFix;
      if (shouldFix) {
        if (this.section) {
          this.section.style.height = `${this.offsetHeight}px`;
        }
        this.classList.add('is-fixed');
        this.style.top = `${topOffset}px`;
      } else {
        if (this.section) {
          this.section.style.height = '';
        }
        this.classList.remove('is-fixed');
        this.style.top = '';
      }
    }

    observeSections() {
      const links = this.querySelectorAll('[data-target]');
      const targetIds = Array.from(links).map((link) => link.getAttribute('data-target'));

      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.setActiveItem(entry.target.id);
            }
          });
        },
        { threshold: this.config.threshold, rootMargin: '-20% 0px -60% 0px' }
      );

      targetIds.forEach((id) => {
        const section = document.getElementById(id);
        if (section) {
          this.sectionObserver.observe(section);
        }
      });
    }

    checkActiveSections() {
      const links = this.querySelectorAll('[data-target]');
      const scrollMarker = window.scrollY + window.innerHeight / 3;
      let activeId = null;

      links.forEach((link) => {
        const targetId = link.getAttribute('data-target');
        const target = document.getElementById(targetId);

        if (target) {
          const rect = target.getBoundingClientRect();
          const top = window.scrollY + rect.top;
          const bottom = top + target.offsetHeight;

          if (scrollMarker >= top && scrollMarker < bottom) {
            activeId = targetId;
          }
        }
      });

      if (activeId && activeId !== this.state.activeId) {
        this.setActiveItem(activeId);
      }
    }

    setActiveItem(id) {
      if (this.state.activeId === id) return;

      this.state.activeId = id;
      this.querySelectorAll('[data-target]').forEach((link) => {
        const isActive = link.getAttribute('data-target') === id;
        link.classList.toggle('active', isActive);
        link.setAttribute('aria-current', isActive ? 'true' : 'false');

        if (isActive && window.innerWidth < 1024) {
          this.scrollItemIntoView(link);
        }
      });
    }

    scrollItemIntoView(link) {
      const nav = this.querySelector('nav');
      if (!nav) return;

      const navRect = nav.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const left = link.offsetLeft - nav.offsetLeft - navRect.width / 2 + linkRect.width / 2;

      nav.scrollTo({ left, behavior: 'smooth' });
    }

    handleClick(event) {
      const link = event.target.closest('[data-target]');
      if (!link) return;

      event.preventDefault();
      const targetId = link.getAttribute('data-target');
      const target = document.getElementById(targetId);

      if (target) {
        const navHeight = this.offsetHeight;
        const area = this.getEffectiveTopOffset();
        const scrollTop =
          window.scrollY + target.getBoundingClientRect().top - navHeight - area - this.config.offsetTop;

        window.scrollTo({ top: scrollTop, behavior: this.config.scrollBehavior });
        this.setActiveItem(targetId);
      }
    }

    throttle(fn, wait) {
      let lastCall = 0;
      return (...args) => {
        const now = Date.now();
        if (now - lastCall >= wait) {
          lastCall = now;
          fn(...args);
        }
      };
    }

    cleanup() {
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
        this.sectionObserver = null;
      }
      if (this.styleObserver) {
        this.styleObserver.disconnect();
        this.styleObserver = null;
      }
    }
  }

  customElements.define('q-anchor-nav', QAnchorNav);
})();
